import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import ResultList from './ResultsList';
import SplashBall from '../../../Splash/splashBall';
import {
    LeaderboardTabTypes,
    LeaderboardParamValues,
    LeaderboardSubtitles,
    RANK_THRESHOLD,
    LeaderboardTabLabels,
} from '../constants';
import { getAgeOrSegmentValue, buildRankingsUrl } from '../helpers';
import styles from '../localizedRankings.scss';
import useStartPowerCheckout from '../../../hooks/useStartPowerCheckout';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import { getRankingsApiAxiosInstance } from '../../../../utils/network';
import { getApiErrorMessage } from '../../../../utils/apiError';
import { showErrorPopup } from '../../../../actions/notification-actions';

const axiosInstance = getRankingsApiAxiosInstance();

const Leaderboard = ({ onBackButtonClick, selectedTab, rankingData }) => {
    const { TOP_ONE_HUNDRED, CLOSE_IN_RANK } = LeaderboardTabTypes;
    const { TENNIS_RATING_TYPE } = LeaderboardParamValues;
    const params = useParams();
    const dispatch = useDispatch();
    const isPowerUser = useSelector((state) => state.auth?.user?.isPowered);
    const currentProfileId = useSelector((state) => state.profile?.playerId);
    const { startPowerCheckout, ctaLabel } = useStartPowerCheckout();

    const [topOneHundredResults, setTopOneHundredResults] = useState({});
    const [closeInRankResults, setCloseInRankResults] = useState({});
    const [isLoadingTopOneHundred, setIsLoadingTopOneHundred] = useState(false);
    const [isLoadingCloseInRank, setIsLoadingCloseInRank] = useState(false);
    const [hasErrorTopOneHundred, setHasErrorTopOneHundred] = useState(false);
    const [hasErrorCloseInRank, setHasErrorCloseInRank] = useState(false);
    const [currentTab, setCurrentTab] = useState(selectedTab);
    const [showSegmentedControl, setShowSegmentedControl] = useState(false);
    const isInitialMount = useRef(true);
    const isMounted = useRef(true);

    const title = `Leaderboard - ${rankingData?.location}, ${rankingData?.formattedCategory}`;
    const subtitle = LeaderboardSubtitles[currentTab];

    const fetchRankings = async (endpointPath, setResults, setIsLoading, setHasError) => {
        if (!isMounted.current) {
            return;
        }

        setIsLoading(true);
        setHasError(false);

        const ageOrSegment = getAgeOrSegmentValue(rankingData?.category, rankingData);
        const url = buildRankingsUrl(endpointPath, TENNIS_RATING_TYPE, rankingData, ageOrSegment);

        try {
            const response = await axiosInstance.get(url);
            if (!isMounted.current) {
                return;
            }

            setResults({
                singles: response?.data?.data?.singles,
                doubles: response?.data?.data?.doubles,
            });
        } catch (error) {
            if (!isMounted.current) {
                return;
            }

            setHasError(true);
            dispatch(showErrorPopup({ error: getApiErrorMessage(error) }));
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const getTopOneHundredRankings = async () => {
        await fetchRankings(
            '/rankings/v1/Community/leaderboard',
            setTopOneHundredResults,
            setIsLoadingTopOneHundred,
            setHasErrorTopOneHundred,
        );
    };

    const getCloseInRankRankings = async () => {
        if (!currentProfileId) {
            setHasErrorCloseInRank(true);
            dispatch(showErrorPopup({ error: 'Profile not found' }));
            setIsLoadingCloseInRank(false);
            return;
        }

        await fetchRankings(
            `/rankings/v1/Community/position/${currentProfileId}`,
            setCloseInRankResults,
            setIsLoadingCloseInRank,
            setHasErrorCloseInRank,
        );
    };

    const dismissLeaderboard = () => {
        setCloseInRankResults({});
        setTopOneHundredResults({});
        setIsLoadingTopOneHundred(false);
        setIsLoadingCloseInRank(false);
        setHasErrorTopOneHundred(false);
        setHasErrorCloseInRank(false);
        onBackButtonClick();
    };
    // Set up cleanup on unmount
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Dismiss leaderboard when navigating to a different user's profile
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const urlPlayerId = parseInt(params?.playerId, 10);
        const profilePlayerId = parseInt(currentProfileId, 10);

        if (urlPlayerId !== profilePlayerId) {
            dismissLeaderboard();
        }
    }, [params?.playerId, currentProfileId, onBackButtonClick]);

    // Fetch rankings when tab is active and data is available
    useEffect(() => {
        if (!rankingData || !isPowerUser) {
            return;
        }

        if (currentTab === TOP_ONE_HUNDRED) {
            const hasResults = topOneHundredResults?.singles?.players?.length > 0 || topOneHundredResults?.doubles?.players?.length > 0;
            if (!isLoadingTopOneHundred && !hasResults && !hasErrorTopOneHundred) {
                getTopOneHundredRankings();
            }
        } else if (currentTab === CLOSE_IN_RANK) {
            const hasResults = closeInRankResults?.singles?.players?.length > 0 || closeInRankResults?.doubles?.players?.length > 0;
            if (!isLoadingCloseInRank && !hasResults && !hasErrorCloseInRank) {
                getCloseInRankRankings();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rankingData, currentTab, isPowerUser]);

    // Reset error flags when rankingData changes to allow retry with new data
    useEffect(() => {
        if (rankingData) {
            setHasErrorTopOneHundred(false);
            setHasErrorCloseInRank(false);

            // Show segmented control if user is not a power user
            if (!isPowerUser) {
                setShowSegmentedControl(true);
                return;
            }

            const singlesRank = rankingData?.singlesRank;
            const doublesRank = rankingData?.doublesRank;
            // Hide segmented control if all existing ranks are outside the threshold
            const shouldHideSegmentedControl =
                (singlesRank === null || singlesRank >= RANK_THRESHOLD) &&
                (doublesRank === null || doublesRank >= RANK_THRESHOLD);
            setShowSegmentedControl(!shouldHideSegmentedControl);
        }
    }, [rankingData, isPowerUser]);

    const handleTabClick = (tab) => {
        setCurrentTab(tab);
        // Reset error flags when switching tabs to allow retry
        if (tab === TOP_ONE_HUNDRED) {
            setHasErrorTopOneHundred(false);
        } else if (tab === CLOSE_IN_RANK) {
            setHasErrorCloseInRank(false);
        }
    };

    const handleBackButtonClick = () => {
        dismissLeaderboard();
    };

    const handleOnUnlockPowerClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.LEADERBOARD,
        });
    };

    return (
        <div className="container" data-testid="profile.leaderboard">
            <div>
                <button type="button" onClick={handleBackButtonClick} className={styles.backButton} data-testid="profile.leaderboard.back-btn">{`\u276E Back`}</button>
            </div>
            {!isPowerUser && (
                <div className={styles.powerContainer}>
                    <p className={styles.powerText} data-testid="profile.leaderboard.power-upsell-text">See where you stack up. Upgrade now!</p>
                    <button type="button" onClick={handleOnUnlockPowerClick} className={styles.powerButton} data-testid="profile.leaderboard.power-upgrade-btn">{ctaLabel('Get Power')}</button>
                </div>
            )}
            <div className={styles.leaderboardContainer}>
                {isLoadingTopOneHundred || isLoadingCloseInRank ? (
                    <div className={styles.loadingContainer} data-testid="profile.leaderboard.loading">
                        <SplashBall size="medium" description="Loading..." />
                    </div>
                ) : (
                    <>
                        <h3 className={styles.leaderboardTitle} data-testid="profile.leaderboard.title">{title}</h3>
                        {showSegmentedControl && (
                            <div className={styles.segmentedControlContainer}>
                                <div className={styles.segmentedControl} data-testid="profile.leaderboard.segmented-control">
                                    <button
                                        type="button"
                                        onClick={() => handleTabClick(CLOSE_IN_RANK)}
                                        className={`${styles.segmentedControlButtonLeft} ${currentTab === CLOSE_IN_RANK ? styles.segmentedControlButtonSelected : ''}`}
                                        data-testid="profile.leaderboard.close-in-rank-btn"
                                    >
                                        {LeaderboardTabLabels[CLOSE_IN_RANK]}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleTabClick(TOP_ONE_HUNDRED)}
                                        className={`${styles.segmentedControlButtonRight} ${currentTab === TOP_ONE_HUNDRED ? styles.segmentedControlButtonSelected : ''}`}
                                        data-testid="profile.leaderboard.top-100-btn"
                                    >
                                        {LeaderboardTabLabels[TOP_ONE_HUNDRED]}
                                    </button>
                                </div>
                            </div>
                        )}
                        <p className={styles.leaderboardSubtitle} data-testid="profile.leaderboard.subtitle">{subtitle}</p>
                        {currentTab === TOP_ONE_HUNDRED && (
                            <ResultList
                                results={topOneHundredResults}
                                isPowerUser={isPowerUser}
                                currentTab={currentTab}
                                rankingType={rankingData?.rankingTypeClicked}
                                showDoubles={rankingData?.showDoubles}
                                showSingles={rankingData?.showSingles}
                            />
                        )}
                        {currentTab === CLOSE_IN_RANK && (
                            <ResultList
                                results={closeInRankResults}
                                isPowerUser={isPowerUser}
                                currentTab={currentTab}
                                rankingType={rankingData?.rankingTypeClicked}
                                showDoubles={rankingData?.showDoubles}
                                showSingles={rankingData?.showSingles}
                            />
                        )}
                    </>
                )}
            </div>
        </div>

    );
};

Leaderboard.propTypes = {
    selectedTab: PropTypes.number.isRequired,
    onBackButtonClick: PropTypes.func.isRequired,
    rankingData: PropTypes.shape({
        ratingType: PropTypes.string.isRequired,
        categoryType: PropTypes.string.isRequired,
        playerId: PropTypes.string.isRequired,
        rankingTypeClicked: PropTypes.string,
    }).isRequired,
};

export default Leaderboard;
